import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash, FileArrowDown, WarningCircle } from "@phosphor-icons/react";
import { downloadCsv } from "./PreTradingTab";
import RuleNote from "./RuleNote";

const SUBS = [
  { id: "day_5to10", label: "Day (5–10 hrs) €19.25" },
  { id: "day_10plus", label: "Day (10 hrs+) €46.17" },
  { id: "overnight_normal", label: "Overnight 1–14 €205.53" },
  { id: "overnight_15_28", label: "Overnight 15–28 €184.98" },
  { id: "overnight_29_56", label: "Overnight 29–56 €102.76" },
];
const TYPES = [
  { id: "travel_subsistence", label: "Travel & Subsistence" },
  { id: "small_benefit", label: "Small Benefit" },
  { id: "remote_working", label: "Remote Working" },
];

const empty = {
  err_type: "travel_subsistence", date_paid: new Date().toISOString().slice(0, 10),
  description: "", subsistence_mode: "calculated", subsistence_rate_key: "day_10plus",
  subsistence_count: 1, days: "", amount: "", reported_ros: false,
};

export default function ERRTab({ onChange }) {
  const [data, setData] = useState({ items: [], summary: {} });
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => { const r = await api.get("/compliance/err"); setData(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (payload.amount === "") delete payload.amount; else payload.amount = Number(payload.amount);
    if (payload.days === "") delete payload.days; else payload.days = Number(payload.days);
    payload.subsistence_count = Number(payload.subsistence_count || 1);
    try {
      await api.post("/compliance/err", payload);
      setForm({ ...empty, err_type: form.err_type });
      load(); onChange && onChange();
    } catch (err) { alert(err?.response?.data?.detail || err.message); }
  };

  const toggle = async (id) => { await api.put(`/compliance/err/${id}/reported`); load(); onChange && onChange(); };
  const del = async (id) => { await api.delete(`/compliance/err/${id}`); load(); onChange && onChange(); };

  const s = data.summary || {};
  const sbPct = Math.min(100, ((s.small_benefit_used || 0) / (s.small_benefit_limit || 1500)) * 100);

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6">
        <div className="eyebrow mb-4">Log an ERR item (real-time reporting to ROS)</div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><label>Category</label>
              <select data-testid="err-type" value={form.err_type} onChange={(e) => setForm({ ...form, err_type: e.target.value })}>{TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select>
            </div>
            <div><label>Date paid</label><input required type="date" data-testid="err-date" value={form.date_paid} onChange={(e) => setForm({ ...form, date_paid: e.target.value })} /></div>
            <div className="md:col-span-2"><label>Description</label><input data-testid="err-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Employee name / reference" /></div>
          </div>

          {form.err_type === "travel_subsistence" && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div><label>Mode</label>
                <select data-testid="err-subs-mode" value={form.subsistence_mode} onChange={(e) => setForm({ ...form, subsistence_mode: e.target.value })}>
                  <option value="calculated">Calculated (rate)</option>
                  <option value="receipted">Receipted / vouched amount</option>
                </select>
              </div>
              {form.subsistence_mode === "calculated" ? (
                <>
                  <div className="md:col-span-2"><label>Rate</label><select data-testid="err-subs-rate" value={form.subsistence_rate_key} onChange={(e) => setForm({ ...form, subsistence_rate_key: e.target.value })}>{SUBS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select></div>
                  <div><label>Count (days/nights)</label><input type="number" min="1" data-testid="err-subs-count" value={form.subsistence_count} onChange={(e) => setForm({ ...form, subsistence_count: e.target.value })} /></div>
                </>
              ) : (
                <div className="md:col-span-2"><label>Receipted amount (€)</label><input required type="number" step="0.01" data-testid="err-subs-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              )}
            </div>
          )}

          {form.err_type === "small_benefit" && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div><label>Benefit value (€)</label><input required type="number" step="0.01" data-testid="err-sb-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="non-cash voucher/gift" /></div>
              <div className="md:col-span-3 text-xs text-white/50 self-center">Max €1,500/yr across 5 benefits. Exceeding the limit makes the entire value taxable.</div>
            </div>
          )}

          {form.err_type === "remote_working" && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div><label>Days worked from home</label><input required type="number" min="1" data-testid="err-rw-days" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} /></div>
              <div className="md:col-span-3 text-xs text-white/50 self-center">€3.20/day tax-free = <span className="mono text-[#FFB020]">€{((Number(form.days) || 0) * 3.2).toFixed(2)}</span></div>
            </div>
          )}

          <div className="flex justify-end"><button type="submit" data-testid="err-add" className="btn-gold"><Plus weight="bold" size={14} /> Log ERR item</button></div>
        </form>
      </div>

      {/* Small benefit tracker */}
      <div className={`glass rounded-xl p-5 ${(s.small_benefit_over_limit || s.small_benefit_over_count) ? "ring-1 ring-[#E63946]/50" : ""}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="eyebrow">Small Benefit Exemption ({s.small_benefit_count || 0}/{s.small_benefit_max_count || 5} benefits)</div>
          <div className="mono text-sm" data-testid="err-sb-remaining">€{Number(s.small_benefit_remaining || 0).toLocaleString()} left</div>
        </div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${sbPct}%`, background: (s.small_benefit_over_limit ? "#E63946" : "linear-gradient(90deg,#00A86B,#FFB020)") }} />
        </div>
        {(s.small_benefit_over_limit || s.small_benefit_over_count) && (
          <div className="flex items-center gap-2 text-[11px] text-[#E63946] mt-2" data-testid="err-sb-warning"><WarningCircle size={14} weight="fill" /> Limit exceeded — the full value is now taxable (no carry-forward).</div>
        )}
      </div>

      <div className="flex items-center justify-end">
        <button onClick={() => downloadCsv("/compliance/err.csv", "err-log.csv")} data-testid="err-export" className="btn-ghost text-sm"><FileArrowDown size={16} /> Export CSV</button>
      </div>

      <div className="glass rounded-xl overflow-auto">
        <table className="data">
          <thead><tr><th>Date paid</th><th>Category</th><th>Description</th><th className="text-right">Amount</th><th>ROS</th><th /></tr></thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan="6" className="text-center text-white/40 py-8">No ERR items logged.</td></tr>
            ) : data.items.map((i) => (
              <tr key={i.entry_id} data-testid={`err-row-${i.entry_id}`}>
                <td className="mono text-xs">{i.date_paid}</td>
                <td className="capitalize">{i.err_type.replace(/_/g, " ")}</td>
                <td className="text-white/70">{i.description || "—"}</td>
                <td className="num">€{Number(i.amount).toFixed(2)}</td>
                <td><button onClick={() => toggle(i.entry_id)} data-testid={`err-ros-${i.entry_id}`} className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${i.reported_ros ? "badge-pass" : "badge-warn"}`}>{i.reported_ros ? "Reported" : "Pending"}</button></td>
                <td><button onClick={() => del(i.entry_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`err-del-${i.entry_id}`}><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RuleNote id="ie_err" />
    </div>
  );
}
